import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, getDocs, limit, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { hasKidsServicesFromCategoryMap, hasMenServicesFromCategoryMap } from '../utils/kidsServices';
import { getCountry, getPrimaryCity } from '../utils/locationHelpers';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

const BATCH_SIZE = 50;

function normalizeText(value) {
  return value?.toLowerCase().trim() || '';
}

/**
 * Compare deux noms de villes pour voir si elles correspondent
 * Gère les cas spéciaux comme les arrondissements (Paris 1er, Paris 15, etc.)
 * @param {string} city1 - Première ville
 * @param {string} city2 - Deuxième ville
 * @returns {boolean} True si les villes correspondent
 */
function citiesMatch(city1, city2) {
  const norm1 = normalizeText(city1);
  const norm2 = normalizeText(city2);
  
  // Correspondance exacte
  if (norm1 === norm2) {
    return true;
  }
  
  // Cas spécial Bruxelles (géré séparément)
  if (isBrusselsKeyword(norm1) && isBrusselsCommune(norm2)) {
    return true;
  }
  if (isBrusselsCommune(norm1) && isBrusselsCommune(norm2)) {
    return true;
  }
  if (isBrusselsKeyword(norm2) && isBrusselsCommune(norm1)) {
    return true;
  }
  
  // Gérer les arrondissements (ex: "Paris 1er", "Paris 15", "Paris 15e")
  // Si une ville contient l'autre comme préfixe suivi d'un espace et d'un nombre/arrondissement
  const arrondissementPattern = /^(.+?)\s+(\d+)(e|er|ème|ème arrondissement)?$/i;
  const match1 = norm1.match(arrondissementPattern);
  const match2 = norm2.match(arrondissementPattern);
  
  if (match1 && match2) {
    // Les deux ont des arrondissements, comparer la ville de base
    return match1[1] === match2[1];
  }
  
  if (match1) {
    // city1 a un arrondissement, comparer avec la ville de base de city2
    return match1[1] === norm2;
  }
  
  if (match2) {
    // city2 a un arrondissement, comparer avec la ville de base de city1
    return norm1 === match2[1];
  }
  
  // Pas de correspondance
  return false;
}

// Communes de la région de Bruxelles-Capitale (pour élargir la recherche "Bruxelles")
const BRUSSELS_COMMUNES = [
  'bruxelles',
  'brussels',
  'anderlecht',
  'auderghem',
  'berchem-sainte-agathe',
  'berchem saint agathe',
  'bethlehem',
  'etterbeek',
  'evere',
  'forest',
  'ganshoren',
  'ixelles',
  'jette',
  'koekelberg',
  'molenbeek',
  'molenbeek-saint-jean',
  'schaerbeek',
  'saint-gilles',
  'saint josse ten noode',
  'saint-josse-ten-noode',
  'uccle',
  'watermael-boitsfort',
  'watermael boitsfort',
  'woluwe-saint-lambert',
  'woluwe saint lambert',
  'woluwe-saint-pierre',
  'woluwe saint pierre',
];

function isBrusselsKeyword(text) {
  const t = normalizeText(text);
  return t === 'bruxelles' || t === 'brussels' || t === 'bruxelles capitale';
}

function isBrusselsCommune(text) {
  const t = normalizeText(text);
  return BRUSSELS_COMMUNES.includes(t);
}

function toLatLng(coords) {
  if (!coords) return null;
  if (typeof coords.lat === 'number' && typeof coords.lng === 'number') {
    return { lat: coords.lat, lng: coords.lng };
  }
  if (typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
    return { lat: coords.latitude, lng: coords.longitude };
  }
  return null;
}

function haversineDistance(coord1, coord2) {
  if (!coord1 || !coord2) return Number.POSITIVE_INFINITY;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(coord2.lat - coord1.lat);
  const dLng = toRad(coord2.lng - coord1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(coord1.lat)) * Math.cos(toRad(coord2.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default function useHairstylists(filters = {}, serviceCategoryMap = {}, providerType = DEFAULT_PROVIDER_TYPE) {
  const { language } = useLanguage();
  const {
    searchText = '',
    selectedServices = [],
    selectedCategories = [],
    selectedCity = '',
    selectedCities = [],
    selectedCountry = '',
    kidsAccepted = false,
    menAccepted = false,
    canTravel = false,
    hasPhotos = false,
    selectedCityCoords = null,
    sortBy = '-',
    sortOrder = 'asc',
  } = filters;

  const [hairstylists, setHairstylists] = useState([]);
  const [allHairstylists, setAllHairstylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [mapsReady, setMapsReady] = useState(false);
  const geocoderRef = useRef(null);
  const cityCoordsCacheRef = useRef({});
  const randomOrderMapRef = useRef(null); // Stocker l'ordre aléatoire en mémoire

  // Utiliser une ref pour serviceCategoryMap pour éviter les re-renders
  const serviceCategoryMapRef = useRef(serviceCategoryMap);
  useEffect(() => {
    serviceCategoryMapRef.current = serviceCategoryMap;
  }, [serviceCategoryMap]);

  // Charger Google Maps et initialiser le geocoder (langue = langue sélectionnée par l'utilisateur)
  useEffect(() => {
    loadGoogleMaps({ requirePlaces: true, language: language || 'fr' })
      .then(() => {
        setMapsReady(true);
        if (window.google?.maps?.Geocoder && !geocoderRef.current) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Impossible de charger Google Maps pour le geocoding.', error);
      });
  }, [language]);

  // Fonction pour geocoder une ville (mainCity)
  const geocodeMainCity = useCallback((city) => {
    return new Promise((resolve) => {
      if (!city || !geocoderRef.current) {
        resolve(null);
        return;
      }

      // Vérifier le cache
      if (cityCoordsCacheRef.current[city]) {
        resolve(cityCoordsCacheRef.current[city]);
        return;
      }

      geocoderRef.current.geocode({ address: city }, (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const location = results[0].geometry.location;
          const coords = { lat: location.lat(), lng: location.lng() };
          cityCoordsCacheRef.current[city] = coords;
          resolve(coords);
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  // Fonction helper pour vérifier si une coiffeuse matche directement une ville (sans canTravel)
  const matchesCityDirectly = useCallback((stylist, citiesToFilter) => {
    if (!citiesToFilter || citiesToFilter.length === 0) return true;
    
    const primaryCity = getPrimaryCity(stylist);
    if (primaryCity) {
      // Vérifier si la ville principale correspond
      const matches = citiesToFilter.some((city) => citiesMatch(city, primaryCity));
      if (matches) return true;
      
      // Vérifier aussi le tableau cities (v1)
      const stylistCities = stylist.cities || [];
      return citiesToFilter.some((city) => {
        return stylistCities.some((sc) => citiesMatch(city, sc));
      });
    } else {
      // Fallback to v1 only
      const stylistCities = stylist.cities || [];
      return citiesToFilter.some((city) => {
        return stylistCities.some((sc) => citiesMatch(city, sc));
      });
    }
  }, []);

  // Fonction de filtrage côté client
  const matchesFilters = useCallback(
    (stylist) => {
      // Exclure les profils avec paymentStatus: 'pending'
      if (stylist.paymentStatus === 'pending') {
        return false;
      }

      // Filtre par pays (supports both v1 and v2) - case insensitive
      if (selectedCountry) {
        const stylistCountry = getCountry(stylist);
        if (!stylistCountry || stylistCountry.toUpperCase() !== selectedCountry.toUpperCase()) {
          return false;
        }
      }

      // Filtre par villes (supports both v1 and v2)
      // Une coiffeuse apparaît SEULEMENT si la ville recherchée est dans son tableau cities
      // Le canTravel est informatif mais ne doit pas être utilisé pour le filtrage
      const citiesToFilter = selectedCities.length > 0 ? selectedCities : (selectedCity ? [selectedCity] : []);
      if (citiesToFilter.length > 0) {
        let matchesCity = false;
        const primaryCity = getPrimaryCity(stylist);
        
        if (primaryCity) {
          // Vérifier si la ville principale correspond
          matchesCity = citiesToFilter.some((city) => citiesMatch(city, primaryCity));
          
          // Si pas de match avec primaryCity, vérifier aussi le tableau cities (v1)
          // Le tableau cities contient la ville principale + les villes additionnelles
          if (!matchesCity) {
            const stylistCities = stylist.cities || [];
            matchesCity = citiesToFilter.some((city) => {
              return stylistCities.some((sc) => citiesMatch(city, sc));
            });
          }
        } else {
          // Fallback to v1 only (pas de primaryCity, utiliser cities)
          const stylistCities = stylist.cities || [];
          matchesCity = citiesToFilter.some((city) => {
            return stylistCities.some((sc) => citiesMatch(city, sc));
          });
        }
        
        // Exclure si la ville recherchée n'est pas dans le tableau cities de la coiffeuse
        // Peu importe si canTravel est true ou false
        if (!matchesCity) {
          return false;
        }
      }

      // Filtre par services
      if (selectedServices.length > 0) {
        const stylistServices = stylist.services || [];
        const matchesService = selectedServices.some((serviceCode) => stylistServices.includes(serviceCode));
        if (!matchesService) {
          return false;
        }
      }

      // Filtre par catégories
      if (selectedCategories.length > 0) {
        const stylistServices = stylist.services || [];
        const currentServiceCategoryMap = serviceCategoryMapRef.current;
        const matchesCategory = stylistServices.some((serviceCode) => {
          const categoryCode = currentServiceCategoryMap[serviceCode];
          return categoryCode && selectedCategories.includes(categoryCode);
        });
        if (!matchesCategory) {
          return false;
        }
      }

      const stylistHasKidsServices =
        stylist.hasKidsServices ??
        hasKidsServicesFromCategoryMap(stylist.services || [], serviceCategoryMapRef.current);

      // Filtre par kidsAccepted
      if (kidsAccepted && !stylistHasKidsServices) {
        return false;
      }

      const stylistHasMenServices =
        hasMenServicesFromCategoryMap(stylist.services || [], serviceCategoryMapRef.current);

      // Filtre par menAccepted
      if (menAccepted && !stylistHasMenServices) {
        return false;
      }

      // Filtre par canTravel
      if (canTravel && !stylist.canTravel) {
        return false;
      }

      // Filtre par photos disponibles
      if (hasPhotos) {
        const stylistHasPhotos =
          Array.isArray(stylist.galleryPhotos) && stylist.galleryPhotos.length > 0;
        if (!stylistHasPhotos) {
          return false;
        }
      }

      // Filtre par texte de recherche (nom de profil + pseudos réseaux)
      // IMPORTANT: Ne pas chercher dans les villes - le filtrage par ville est géré séparément
      if (searchText) {
        const loweredSearch = normalizeText(searchText);
        
        // Si une ville est sélectionnée, ne pas utiliser searchText pour filtrer
        // (le filtrage par ville est déjà fait plus haut)
        const citiesToFilter = selectedCities.length > 0 ? selectedCities : (selectedCity ? [selectedCity] : []);
        if (citiesToFilter.length > 0) {
          // Une ville est sélectionnée, donc searchText ne doit pas être utilisé pour filtrer
          // (pour éviter de trouver "waterloo" dans le nom quand on filtre par ville "waterloo")
          // Ne rien faire - le filtrage par ville est déjà appliqué
        } else {
          // Aucune ville sélectionnée, on peut utiliser searchText
          // MAIS: si searchText correspond à une ville de la coiffeuse, on l'ignore
          // (pour éviter de trouver des coiffeuses qui ont "waterloo" dans leur nom mais ne sont pas à Waterloo)
          const primaryCity = getPrimaryCity(stylist);
          const stylistCities = stylist.cities || [];
          const allStylistCities = primaryCity ? [primaryCity, ...stylistCities] : stylistCities;
          const cityMatch = allStylistCities.some((city) => {
            // Utiliser la même logique stricte de matching que pour le filtre de ville
            return citiesMatch(searchText, city);
          });
          
          // Si searchText correspond à une ville de la coiffeuse, ignorer searchText
          // (pour éviter de trouver "waterloo" dans le nom quand on cherche la ville "waterloo")
          if (cityMatch) {
            // Ne rien faire - on ne veut pas filtrer par searchText si c'est une ville
          } else {
            // Chercher dans le nom, description, Instagram, TikTok uniquement
            const nameMatch = normalizeText(stylist.name || '').includes(loweredSearch);

            // Pseudos Instagram / TikTok possibles à deux emplacements (champ direct ou dans contact)
            const instagramPseudo = normalizeText(
              stylist.instagram || stylist.contact?.instagram || '',
            );
            const tiktokPseudo = normalizeText(stylist.tiktok || stylist.contact?.tiktok || '');

            const instagramMatch = instagramPseudo.includes(loweredSearch);
            const tiktokMatch = tiktokPseudo.includes(loweredSearch);

            const descriptionMatch = normalizeText(stylist.description || '').includes(loweredSearch);

            if (!nameMatch && !instagramMatch && !tiktokMatch && !descriptionMatch) {
              return false;
            }
          }
        }
      }

      return true;
    },
    [
      selectedCity,
      selectedCities,
      selectedCountry,
      selectedServices,
      selectedCategories,
      kidsAccepted,
      menAccepted,
      canTravel,
      hasPhotos,
      searchText,
    ],
  );

  const fetchAll = useCallback(async () => {
      setLoading(true);
      setError(null);
    try {
      const collectedDocs = [];
      let lastDoc = null;
      let moreDocs = true;

      while (moreDocs) {
        // Filtrer par type de prestataire
        // Pour 'hair', on ne filtre pas pour rétrocompatibilité (documents sans champ 'type')
        let q;
        if (providerType === 'hair' || providerType === DEFAULT_PROVIDER_TYPE) {
          // Pas de filtre par type pour 'hair' - rétrocompatibilité
          q = lastDoc
            ? query(collection(db, 'hairstylists'), startAfter(lastDoc), limit(BATCH_SIZE))
            : query(collection(db, 'hairstylists'), limit(BATCH_SIZE));
        } else {
          // Filtre par type pour les autres types (nails, makeup, eyelash)
          q = lastDoc
            ? query(collection(db, 'hairstylists'), where('type', '==', providerType), startAfter(lastDoc), limit(BATCH_SIZE))
            : query(collection(db, 'hairstylists'), where('type', '==', providerType), limit(BATCH_SIZE));
        }

        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          break;
        }

        lastDoc = snapshot.docs[snapshot.docs.length - 1];
        collectedDocs.push(
          ...snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          })),
        );

        if (snapshot.size < BATCH_SIZE) {
          moreDocs = false;
        }
      }

      // Enrichir tous les docs (sans filtrage) pour la carte
      const enrichedDocs = collectedDocs.map((doc) => ({
        ...doc,
        hasKidsServices:
          doc.hasKidsServices ??
          hasKidsServicesFromCategoryMap(doc.services || [], serviceCategoryMapRef.current),
      }));

      // Liste complète (toutes les coiffeuses) pour la vue carte
      // Exclure les profils sans nom valide et ceux en attente de paiement
      // Pour le type 'hair', filtrer côté client pour exclure les profils d'autres types
      const validAllDocs = enrichedDocs.filter((doc) => {
        const name = doc.name;
        if (!name || typeof name !== 'string' || name.trim().length === 0) return false;
        // Exclure les profils en attente de paiement
        if (doc.paymentStatus === 'pending') return false;
        // Pour 'hair', inclure seulement les profils sans type ou avec type === 'hair'
        if (providerType === 'hair' || providerType === DEFAULT_PROVIDER_TYPE) {
          return !doc.type || doc.type === 'hair';
        }
        return true;
      });
      setAllHairstylists(validAllDocs);

      // Générer un ordre aléatoire une fois basé sur tous les documents (pas seulement les filtrés)
      // pour qu'il soit stable même après filtrage
      if (!randomOrderMapRef.current && enrichedDocs.length > 0) {
        const allIds = enrichedDocs.map((doc) => doc.id).sort();
        const randomOrder = [...allIds].sort(() => Math.random() - 0.5);
        randomOrderMapRef.current = new Map();
        randomOrder.forEach((id, index) => {
          randomOrderMapRef.current.set(id, index);
        });
      }

      // Filtrage pour la vue liste uniquement
      // Exclure les profils sans nom valide
      // Pour le type 'hair', filtrer côté client pour exclure les profils d'autres types
      const validDocs = enrichedDocs.filter((doc) => {
        const name = doc.name;
        if (!name || typeof name !== 'string' || name.trim().length === 0) return false;
        // Pour 'hair', inclure seulement les profils sans type ou avec type === 'hair'
        if (providerType === 'hair' || providerType === DEFAULT_PROVIDER_TYPE) {
          return !doc.type || doc.type === 'hair';
        }
        return true;
      });
      const filteredDocs = validDocs.filter(matchesFilters);

      // Toutes les coiffeuses qui passent le filtre sont dans la ville recherchée
      // (le filtrage par ville est déjà fait dans matchesFilters)
      // Plus besoin de séparer directMatches et travelMatches
      const allMatches = [...filteredDocs];

      // Trier tous les matches ensemble
      const sortedMatches = [...allMatches];
      const orderMultiplier = sortOrder === 'asc' ? 1 : -1;

      if (sortBy === '-') {
        // Tri par défaut : d'abord PRO, puis FREE
        // Parmi les PRO : highest note first, puis last PRO upgrade first (subscriptionStartDate)
        // Parmi les FREE : tri par notes, puis par dernière création
        sortedMatches.sort((a, b) => {
          const isProA = (a.accountType || 'PRO') === 'PRO';
          const isProB = (b.accountType || 'PRO') === 'PRO';

          // Placer les PRO avant les FREE
          if (isProA && !isProB) return -1;
          if (!isProA && isProB) return 1;

          // Si les deux sont PRO, trier par highest note first, puis last PRO upgrade first
          if (isProA && isProB) {
            const ratingA = a.ratingSummary?.averageRating ?? 0;
            const ratingB = b.ratingSummary?.averageRating ?? 0;

            // Highest note first (décroissant)
            if (ratingA !== ratingB) {
              return ratingB - ratingA; // Décroissant : note la plus élevée en premier
            }

            // Si notes égales, trier par last PRO upgrade first (décroissant)
            const upgradeDateA = (() => {
              const upgradeDate =
                a.subscriptionStartDate?.toMillis?.() ??
                (a.subscriptionStartDate?.seconds ? a.subscriptionStartDate.seconds * 1000 : null);
              // Si pas de subscriptionStartDate, utiliser createdAt comme fallback
              if (!upgradeDate) {
                const created =
                  a.createdAt?.toMillis?.() ??
                  (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt ?? 0));
                return created || 0;
              }
              return upgradeDate;
            })();

            const upgradeDateB = (() => {
              const upgradeDate =
                b.subscriptionStartDate?.toMillis?.() ??
                (b.subscriptionStartDate?.seconds ? b.subscriptionStartDate.seconds * 1000 : null);
              // Si pas de subscriptionStartDate, utiliser createdAt comme fallback
              if (!upgradeDate) {
                const created =
                  b.createdAt?.toMillis?.() ??
                  (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt ?? 0));
                return created || 0;
              }
              return upgradeDate;
            })();

            // Last PRO upgrade first (décroissant) : upgradeDateB - upgradeDateA
            const dateDiff = upgradeDateB - upgradeDateA;
            if (dateDiff !== 0) return dateDiff;

            // Id pour stabilité
            return a.id.localeCompare(b.id);
          }

          // Si les deux sont FREE, trier par notes puis par date (logique originale)
          const hasRatingA = (a.ratingSummary?.reviewCount ?? 0) > 0;
          const hasRatingB = (b.ratingSummary?.reviewCount ?? 0) > 0;

          // Placer les profils notés avant les non notés
          if (hasRatingA && !hasRatingB) return -1;
          if (!hasRatingA && hasRatingB) return 1;

          // Si les deux sont notés ou les deux ne le sont pas, trier par notes
          const ratingA = a.ratingSummary?.averageRating ?? 0;
          const ratingB = b.ratingSummary?.averageRating ?? 0;

          if (ratingA !== ratingB) {
            return (ratingA - ratingB) * orderMultiplier;
          }

          // Si notes égales, trier par nombre d'avis
          const countA = a.ratingSummary?.reviewCount ?? 0;
          const countB = b.ratingSummary?.reviewCount ?? 0;
          if (countA !== countB) {
            return (countA - countB) * orderMultiplier;
          }

          // Si notes et nombre d'avis égaux, trier par dernière création (createdAt)
          const dateA = (() => {
            const created =
              a.createdAt?.toMillis?.() ??
              (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt ?? 0));
            return created || 0;
          })();

          const dateB = (() => {
            const created =
              b.createdAt?.toMillis?.() ??
              (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt ?? 0));
            return created || 0;
          })();

          // Dernière création en premier (desc) : dateB - dateA
          const dateDiff = dateB - dateA;
          if (dateDiff !== 0) return dateDiff;

          // Id pour stabilité
          return a.id.localeCompare(b.id);
        });
      } else if (sortBy === 'distance' && selectedCityCoords && mapsReady) {
        // Geocoder toutes les villes principales et trier (supports both v1 and v2)
        const allDocsForGeocode = [...sortedMatches];
        const geocodePromises = allDocsForGeocode.map(async (doc) => {
          const primaryCity = getPrimaryCity(doc);
          if (!primaryCity) {
            return { doc, coords: null };
          }
          const coords = await geocodeMainCity(primaryCity);
          return { doc, coords };
        });

        const docsWithCoords = await Promise.all(geocodePromises);
        
        const sortByDistance = (arr) => {
          arr.sort((a, b) => {
            const aData = docsWithCoords.find((item) => item.doc.id === a.id);
            const bData = docsWithCoords.find((item) => item.doc.id === b.id);
            const coordsA = aData?.coords;
            const coordsB = bData?.coords;
            if (!coordsA && !coordsB) return 0;
            if (!coordsA) return 1;
            if (!coordsB) return -1;
            const distA = haversineDistance(toLatLng(coordsA), selectedCityCoords);
            const distB = haversineDistance(toLatLng(coordsB), selectedCityCoords);
            return (distA - distB) * orderMultiplier;
          });
        };
        
        sortByDistance(sortedMatches);
      } else if (sortBy === 'rating') {
        const sortByRating = (arr) => {
          arr.sort((a, b) => {
            const hasRatingA = (a.ratingSummary?.reviewCount ?? 0) > 0;
            const hasRatingB = (b.ratingSummary?.reviewCount ?? 0) > 0;
            if (hasRatingA && !hasRatingB) return -1;
            if (!hasRatingA && hasRatingB) return 1;
            const getDateVal = (doc) => {
              const updated = doc.updatedAt?.toMillis?.() ?? (doc.updatedAt?.seconds ? doc.updatedAt.seconds * 1000 : (doc.updatedAt ?? 0));
              if (updated) return updated;
              const created = doc.createdAt?.toMillis?.() ?? (doc.createdAt?.seconds ? doc.createdAt.seconds * 1000 : (doc.createdAt ?? 0));
              return created || 0;
            };
            const ratingA = a.ratingSummary?.averageRating ?? 0;
            const ratingB = b.ratingSummary?.averageRating ?? 0;
            if (ratingB !== ratingA) return (ratingA - ratingB) * orderMultiplier;
            const countA = a.ratingSummary?.reviewCount ?? 0;
            const countB = b.ratingSummary?.reviewCount ?? 0;
            if (countB !== countA) return (countA - countB) * orderMultiplier;
            const dateA = getDateVal(a);
            const dateB = getDateVal(b);
            const dateDiff = (dateB - dateA) * orderMultiplier;
            if (dateDiff !== 0) return dateDiff;
            return a.id.localeCompare(b.id);
          });
        };
        sortByRating(sortedMatches);
      } else if (sortBy === 'alphabetical') {
        const sortAlphabetical = (arr) => {
          arr.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            const comparison = nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
            return comparison * orderMultiplier;
          });
        };
        sortAlphabetical(sortedMatches);
      } else if (sortBy === 'createdAt') {
        const sortByCreatedAt = (arr) => {
          arr.sort((a, b) => {
            const dateA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt || 0));
            const dateB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt || 0));
            if (sortOrder === 'asc') return dateA - dateB;
            return dateB - dateA;
          });
        };
        sortByCreatedAt(sortedMatches);
      } else if (selectedCityCoords && mapsReady) {
        const allDocsForGeocode = [...sortedMatches];
        const geocodePromises = allDocsForGeocode.map(async (doc) => {
          const primaryCity = getPrimaryCity(doc);
          if (!primaryCity) return { doc, coords: null };
          const coords = await geocodeMainCity(primaryCity);
          return { doc, coords };
        });
        const docsWithCoords = await Promise.all(geocodePromises);
        const sortByDistance = (arr) => {
          arr.sort((a, b) => {
            const aData = docsWithCoords.find((item) => item.doc.id === a.id);
            const bData = docsWithCoords.find((item) => item.doc.id === b.id);
            const coordsA = aData?.coords;
            const coordsB = bData?.coords;
            if (!coordsA && !coordsB) return 0;
            if (!coordsA) return 1;
            if (!coordsB) return -1;
            const distA = haversineDistance(toLatLng(coordsA), selectedCityCoords);
            const distB = haversineDistance(toLatLng(coordsB), selectedCityCoords);
            return (distA - distB) * orderMultiplier;
          });
        };
        sortByDistance(sortedMatches);
      }

      // Toujours afficher les PRO en premier, puis les autres (quel que soit le tri)
      const isPro = (doc) => (doc.accountType || 'PRO') === 'PRO';
      const proList = sortedMatches.filter(isPro);
      const otherList = sortedMatches.filter((d) => !isPro(d));
      const sortedDocs = [...proList, ...otherList];

      setHairstylists(sortedDocs);
      setTotalCount(sortedDocs.length);
      } catch (err) {
          setError(err);
      } finally {
          setLoading(false);
      }
  }, [matchesFilters, selectedCityCoords, sortBy, sortOrder, mapsReady, geocodeMainCity, providerType, matchesCityDirectly]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const loadMore = useCallback(() => {}, []);

  return {
    hairstylists,      // PRO d’abord, puis les autres (liste complète)
    allHairstylists,   // complètes (vue carte)
    loading,
    error,
    loadMore,
    hasMore: false,
    totalCount,
  };
}
